/**
 * Saldo por protocolo: quanto foi cobrado, quanto entrou, quanto falta.
 *
 *     total cobrado  −  recebido  −  glosado  =  saldo a receber
 *
 * O total cobrado vem do Bioslab e é o tamanho do bolo; recebido e glosado
 * o sistema já apurava dos arquivos da operadora. O que sobra é dinheiro
 * faturado que a operadora ainda não pagou e também não recusou.
 *
 * **O que esta tela recusa a fazer:** mostrar saldo negativo como se fosse
 * saldo. Em protocolos reais a operadora pagou MAIS do que o Bioslab
 * registra como cobrado — é a divergência das três tabelas de preço — e a
 * conta dá negativo. Chamar isso de "a receber" inverteria o sinal de uma
 * inconsistência. São estados separados, com nomes diferentes.
 *
 * E "sem total" não é "zero a receber": é "o Bioslab ainda não foi
 * consultado". Tratar os dois como a mesma coisa faria o painel afirmar que
 * não há nada a receber quando o que há é falta de informação.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import {
  filtrosDeSaldo,
  historicoDeSaldo,
  limparSaldosOrfaos,
  sincronizarSaldo,
  visaoSaldo,
} from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Badge,
  Botao,
  Indicador,
  LoadingSpinner,
  PageHeader,
  Paginacao,
  Painel,
  SelecaoBuscavel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { useOrdenacao } from '../hooks/useOrdenacao';
import { usePaginacao } from '../hooks/usePaginacao';
import { brl, formatDateTime, numero } from '../utils/formato';

const ESTADO = {
  a_receber: { rotulo: 'A receber', tom: 'pendente' },
  quitado: { rotulo: 'Quitado', tom: 'confirmado' },
  inconsistente: { rotulo: 'Inconsistente', tom: 'reprovado' },
  sem_total: { rotulo: 'Sem total', tom: 'arquivado' },
};

/* A barra que mostra a composição do protocolo de uma olhada: o quanto já
   entrou, o quanto foi recusado, e o que resta. Vale mais que a leitura dos
   três números porque a proporção é a informação. */
function Composicao({ linha }) {
  const total = Number(linha.total_cobrado || 0);
  if (!total) return <span style={{ color: 'var(--ff-border-field)' }}>—</span>;

  const pct = (v) => Math.max(0, Math.min(100, (Number(v || 0) / total) * 100));
  const faixas = [
    { v: pct(linha.recebido), cor: 'var(--ff-green)', nome: 'recebido' },
    { v: pct(linha.glosado), cor: 'var(--ff-error-ink)', nome: 'glosado' },
  ];
  const resto = Math.max(0, 100 - faixas[0].v - faixas[1].v);

  return (
    <div
      title={`recebido ${brl(linha.recebido)} · glosado ${brl(linha.glosado)} · falta ${brl(
        linha.saldo
      )}`}
      style={{
        display: 'flex',
        height: 8,
        borderRadius: 999,
        overflow: 'hidden',
        background: 'var(--ff-border-soft)',
        minWidth: 110,
      }}
    >
      {faixas.map((f) => (
        <div key={f.nome} style={{ width: `${f.v}%`, background: f.cor }} />
      ))}
      <div style={{ width: `${resto}%` }} />
    </div>
  );
}

/* As linhas-filhas de um protocolo: uma por competência em que ele recebeu
   ou foi glosado.

   Por que o detalhe desceu para cá em vez de ficar na própria linha: um
   protocolo recebe em várias competências — o pagamento parcial é a regra —,
   e empilhar tudo na célula fazia a tabela crescer na horizontal e na
   vertical ao mesmo tempo. Com a expansão, o primeiro nível responde "quanto
   falta" numa linha só, e quem quer saber "quando entrou" abre. */
function LinhasDaCompetencia({ linha }) {
  if (!linha.competencias.length) {
    return (
      <tr className="ff-linha-filha">
        <td colSpan={11} style={{ paddingLeft: 44, color: 'var(--ff-muted)', fontSize: 13 }}>
          Nenhum recebimento registrado para este protocolo ainda.
        </td>
      </tr>
    );
  }
  return linha.competencias.map((c) => (
    <tr key={c.competencia} className="ff-linha-filha">
      <td style={{ paddingLeft: 44 }} className="col-meta">
        <i
          className="bi bi-arrow-return-right me-2"
          aria-hidden="true"
          style={{ color: 'var(--ff-border-field)' }}
        />
        <span className="ff-mono">{c.competencia || '—'}</span>
      </td>
      <td className="col-meta">{c.operadora || '—'}</td>
      <td className="col-valor" style={{ color: 'var(--ff-border-field)' }}>
        —
      </td>
      <td className="col-valor">{brl(c.recebido)}</td>
      <td className="col-valor" style={{ color: 'var(--ff-border-field)' }}>
        —
      </td>
      <td className="col-valor">
        {c.glosado === null || c.glosado === undefined ? (
          <span style={{ color: 'var(--ff-border-field)' }}>—</span>
        ) : Number(c.glosado) > 0 ? (
          <span style={{ color: 'var(--ff-error-ink)' }}>{brl(c.glosado)}</span>
        ) : (
          brl(c.glosado)
        )}
      </td>
      {/* O saldo NÃO se reparte por competência: ele é do protocolo.
          Repetir ou ratear aqui inventaria um número que não existe. */}
      <td className="col-valor" style={{ color: 'var(--ff-border-field)' }}>
        —
      </td>
      <td className="col-qtd" style={{ color: 'var(--ff-muted)', fontSize: 12 }}>
        {numero(c.guias)} guia{c.guias === 1 ? '' : 's'}
      </td>
      {/* Composicao, Situacao e o botao de detalhe nao se repetem por
          competencia: eles descrevem o protocolo inteiro. As celulas vazias
          existem para a sub-linha ter a MESMA contagem do cabecalho — sem
          elas as colunas saem deslocadas, que era o caso antes da coluna de
          acao entrar e ficaria pior com ela. */}
      <td />
      <td />
      <td />
    </tr>
  ));
}

export default function Saldo() {
  // Disparar a consulta ao Bioslab e carga na producao do cliente: quem
  // aciona e quem responde por ela. O operador continua vendo o saldo
  // inteiro — so nao dispara.
  const { isGestor } = useAuth();
  const [params, setParams] = useSearchParams();
  const navegar = useNavigate();
  const [dados, setDados] = useState(null);
  const [opcoes, setOpcoes] = useState(null);
  const [evolucao, setEvolucao] = useState([]);
  const [abertos, setAbertos] = useState(() => new Set());
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [sincronizando, setSincronizando] = useState(false);
  const [limpando, setLimpando] = useState(false);

  const filtros = useMemo(
    () => ({
      operadora: params.get('operadora') || undefined,
      competencia: params.get('competencia') || undefined,
      estado: params.get('estado') || undefined,
    }),
    [params]
  );

  const alternar = useCallback((protocolo) => {
    setAbertos((atual) => {
      const novo = new Set(atual);
      if (novo.has(protocolo)) novo.delete(protocolo);
      else novo.add(protocolo);
      return novo;
    });
  }, []);

  const trocar = useCallback(
    (campo, valor) => {
      const novo = new URLSearchParams(params);
      if (valor) novo.set(campo, valor);
      else novo.delete(campo);
      setParams(novo);
    },
    [params, setParams]
  );

  const carregar = useCallback(() => {
    setDados(null);
    visaoSaldo(filtros)
      .then((r) => setDados(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar o saldo.')));
  }, [filtros]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    historicoDeSaldo()
      .then((r) => setEvolucao(r.data.evolucao || []))
      .catch(() => setEvolucao([]));
  }, [dados]);

  // Em cascata: os filtros atuais vao junto, e o backend devolve so o que
  // existe neste recorte — cada dimensao ignorando a si mesma, para que dê
  // para trocar sem antes limpar. Sem passar os filtros, a cascata existiria
  // no servidor e nao funcionaria na tela.
  useEffect(() => {
    filtrosDeSaldo(filtros)
      .then((r) => setOpcoes(r.data))
      .catch(() => setOpcoes({ operadoras: [], competencias: [], estados: [] }));
  }, [filtros, dados]);

  const sincronizar = async () => {
    setSincronizando(true);
    setErro('');
    try {
      const { data } = await sincronizarSaldo();
      setAviso(
        `${numero(data.atualizados)} protocolo(s) atualizados a partir do Bioslab` +
          (data.suspeitos
            ? ` · ${numero(data.suspeitos)} com total suspeito de duplicação`
            : '')
      );
      carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível consultar o Bioslab.'));
    } finally {
      setSincronizando(false);
    }
  };

  // Ordena ANTES de paginar. O contrário ordenaria só a página aberta, e o
  // maior saldo da base poderia ficar na página 3 com a tabela dizendo estar
  // ordenada por saldo — o pior tipo de erro numa tela de cobrança.
  const ord = useOrdenacao(dados?.linhas || [], 'saldo', false);
  // Hook PRÓPRIO para o recorte por operadora. Antes ele reusava o `ord` da
  // tabela principal: clicar no cabeçalho dali reordenava a lista de baixo e
  // deixava esta intacta — a setinha acendia na tabela errada.
  const ordOp = useOrdenacao(dados?.por_operadora || [], 'saldo', false);

  // Paginada: a lista cresce com o número de protocolos conciliados, e uma
  // tabela de centenas de linhas com expansão vira rolagem infinita.
  const pag = usePaginacao(ord.ordenados, 25);

  // Protocolo cujo arquivo foi apagado: o total do Bioslab continua aqui,
  // com recebido zero, afirmando um "a receber" que nada sustenta. Dá para
  // reconhecê-lo pela tela — é a linha sem operadora e sem competência.
  const orfaos = (dados?.linhas || []).filter(
    (l) => !l.operadora && !l.competencias.length && l.total_cobrado !== null
  );

  const limpar = async () => {
    if (
      !window.confirm(
        `Apagar o total cobrado de ${orfaos.length} protocolo(s) que não têm mais nenhum arquivo lançado?` +
          ' O valor é reconstituível: "Atualizar totais" o traz de volta assim ' +
          'que houver arquivo de novo. O histórico de apurações não é tocado.'
      )
    )
      return;
    setLimpando(true);
    setErro('');
    try {
      const { data } = await limparSaldosOrfaos();
      setAviso(
        `${numero(data.removidos)} protocolo(s) sem arquivo removido(s) · ` +
          `${brl(data.total_removido)} de total cobrado que ninguém sustentava`
      );
      carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível limpar.'));
    } finally {
      setLimpando(false);
    }
  };

  const r = dados?.resumo;
  const semTotal = r?.sem_total || 0;

  return (
    <>
      <PageHeader
        titulo="Saldo por protocolo"
        subtitulo="Quanto foi cobrado, quanto já entrou, e quanto ainda há para receber."
        acoes={
          isGestor && (
            <div className="d-flex flex-wrap gap-2">
              {/* Só aparece quando há o que limpar — um botão destrutivo
                  permanentemente visível convida ao clique distraído. */}
              {orfaos.length > 0 && (
                <Botao
                  variante="secundario"
                  onClick={limpar}
                  disabled={limpando || sincronizando}
                  icone="bi-eraser"
                  title={
                    `${orfaos.length} protocolo(s) com total do Bioslab mas sem ` +
                    'nenhum arquivo lançado. O valor é reconstituível.'
                  }
                >
                  {limpando
                    ? 'Limpando…'
                    : `Excluir dados sem arquivo (${orfaos.length})`}
                </Botao>
              )}
              <Botao
                onClick={sincronizar}
                disabled={sincronizando || limpando}
                icone="bi-arrow-repeat"
              >
                {sincronizando ? 'Consultando o Bioslab…' : 'Atualizar totais'}
              </Botao>
            </div>
          )
        }
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>
      <Alerta tipo="ok" onFechar={() => setAviso('')}>
        {aviso}
      </Alerta>

      {semTotal > 0 && (
        <Alerta tipo="atencao">
          <strong>
            {numero(semTotal)} protocolo(s) ainda sem o total cobrado do Bioslab.
          </strong>{' '}
          Para eles o saldo não é zero — é desconhecido.{' '}
          {isGestor ? (
            <>
              Clique em <strong>Atualizar totais</strong> para buscá-los. A consulta vai
              apenas aos protocolos que já apareceram em algum arquivo lançado, nunca à
              base inteira.
            </>
          ) : (
            <>Peça a um gestor para atualizar os totais.</>
          )}
        </Alerta>
      )}

      {Number(r?.aguardando_revisao || 0) > 0 && (
        <Alerta tipo="info">
          <strong>{brl(r.aguardando_revisao)} já extraídos aguardam a revisão
          humana</strong> e por isso <strong>não</strong> estão contados como recebidos.
          É a mesma regra da Baixa por protocolo: dado extraído só vira oficial depois
          que alguém confere. Confirme a revisão em <strong>Arquivos</strong> e o valor
          entra aqui.
        </Alerta>
      )}

      {r && (
        <div className="ff-grid-tiles mb-4">
          <Indicador
            natureza="financeiro"
            rotulo="Total cobrado"
            valor={brl(r.total_cobrado)}
            nota={`${numero(r.protocolos)} protocolo(s)`}
          />
          <Indicador
            natureza="entrada"
            rotulo="Já recebido"
            valor={brl(r.recebido)}
            nota={
              Number(r.total_cobrado) > 0
                ? `${Math.round((Number(r.recebido) / Number(r.total_cobrado)) * 100)}% do cobrado`
                : 'sem total para comparar'
            }
          />
          <Indicador
            natureza="atencao"
            rotulo="Glosado"
            valor={brl(r.glosado)}
            nota="recusado pela operadora"
          />
          <Indicador
            natureza="neutro"
            rotulo="Ainda a receber"
            valor={brl(r.a_receber)}
            nota={`${numero(r.quitados)} quitado(s) · ${numero(r.inconsistentes)} inconsistente(s)`}
          />
        </div>
      )}

      {/* Por operadora: a pergunta de gestão — "quanto a Unimed ainda me
          deve?" — que a lista por protocolo responde devagar. */}
      {dados?.por_operadora?.length > 0 && (
        <div className="mb-3">
        <Painel titulo="Por operadora" subtitulo="Quanto falta receber de cada uma">
          <Tabela estreita>
            <thead>
              <tr>
                <Th campo="operadora" estado={ordOp}>Operadora</Th>
                <Th campo="qtd_protocolos" estado={ordOp} alinhar="right">
                  Protocolos
                </Th>
                <Th campo="total_cobrado" estado={ordOp} alinhar="right">
                  Cobrado
                </Th>
                <Th campo="recebido" estado={ordOp} alinhar="right">
                  Recebido
                </Th>
                <Th campo="glosado" estado={ordOp} alinhar="right">
                  Glosado
                </Th>
                <Th campo="saldo" estado={ordOp} alinhar="right">
                  A receber
                </Th>
              </tr>
            </thead>
            <tbody>
              {ordOp.ordenados.map((o) => (
                <tr key={o.operadora}>
                  <td className="col-principal">
                    {o.operadora}
                    {o.inconsistentes > 0 && (
                      <span style={{ marginLeft: 8 }}>
                        <Badge tom="reprovado">
                          {o.inconsistentes} inconsistente(s)
                        </Badge>
                      </span>
                    )}
                  </td>
                  <td className="col-qtd">{numero(o.protocolos)}</td>
                  <td className="col-valor">{brl(o.total_cobrado)}</td>
                  <td className="col-valor">{brl(o.recebido)}</td>
                  <td className="col-valor">{brl(o.glosado)}</td>
                  <td className="col-valor">
                    <strong>{brl(o.a_receber)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        </Painel>
        </div>
      )}

      {/* Em quais competências entrou: responde "quando recebi", que o saldo
          sozinho não responde. */}
      {dados?.por_competencia?.length > 0 && (
        <div className="mb-3">
        <Painel
          titulo="Entradas por competência"
          subtitulo="Quando o dinheiro chegou, somando todos os protocolos"
        >
          <div className="p-3 d-flex flex-column gap-2">
            {(() => {
              const maior = Math.max(
                ...dados.por_competencia.map((c) => Number(c.recebido || 0))
              );
              return dados.por_competencia.map((c) => (
                <div key={c.competencia} className="d-flex align-items-center gap-3">
                  <span className="ff-mono" style={{ minWidth: 72, fontSize: 13 }}>
                    {c.competencia}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: 22,
                      background: 'var(--ff-border-soft)',
                      borderRadius: 6,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${maior ? (Number(c.recebido) / maior) * 100 : 0}%`,
                        height: '100%',
                        background:
                          'linear-gradient(90deg, var(--ff-blue), var(--ff-green))',
                      }}
                    />
                  </div>
                  <span className="ff-mono" style={{ minWidth: 110, textAlign: 'right' }}>
                    {brl(c.recebido)}
                  </span>
                  <span
                    style={{ minWidth: 150, fontSize: 12, color: 'var(--ff-muted)' }}
                  >
                    {numero(c.protocolos)} protocolo(s) · {numero(c.guias)} guias
                  </span>
                </div>
              ));
            })()}
          </div>
        </Painel>
        </div>
      )}

      {/* O valor corrente nao diz se a cobranca esta funcionando; a
          sequencia diz. So aparece com duas apuracoes — com uma, uma "curva"
          de um ponto nao e curva, e sugeriria tendencia onde nao ha. */}
      {evolucao.length > 1 && (
        <div className="mb-3">
          <Painel
            titulo="O saldo ao longo do tempo"
            subtitulo="Uma foto a cada atualização de totais — é o que mostra se o que falta receber está caindo"
          >
            <div className="p-3 d-flex flex-column gap-2">
              {(() => {
                const maior = Math.max(
                  ...evolucao.map((p) => Number(p.a_receber || 0)),
                  1
                );
                return evolucao.map((p, i) => {
                  const anterior = i > 0 ? Number(evolucao[i - 1].a_receber || 0) : null;
                  const atual = Number(p.a_receber || 0);
                  const delta = anterior === null ? null : atual - anterior;
                  return (
                    <div
                      key={p.apurado_em || i}
                      className="d-flex align-items-center gap-3"
                    >
                      <span
                        className="ff-mono"
                        style={{ minWidth: 130, fontSize: 12 }}
                      >
                        {formatDateTime(p.apurado_em)}
                      </span>
                      <div
                        style={{
                          flex: 1,
                          height: 18,
                          background: 'var(--ff-border-soft)',
                          borderRadius: 6,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${(atual / maior) * 100}%`,
                            height: '100%',
                            background: 'var(--ff-navy)',
                          }}
                        />
                      </div>
                      <span
                        className="ff-mono"
                        style={{ minWidth: 110, textAlign: 'right' }}
                      >
                        {brl(atual)}
                      </span>
                      <span
                        style={{
                          minWidth: 110,
                          fontSize: 12,
                          textAlign: 'right',
                          color:
                            delta === null
                              ? 'var(--ff-muted)'
                              : delta < 0
                                ? 'var(--ff-green)'
                                : 'var(--ff-error-ink)',
                        }}
                      >
                        {delta === null
                          ? `${numero(p.protocolos)} protocolo(s)`
                          : delta === 0
                            ? 'sem mudança'
                            : `${delta < 0 ? '↓' : '↑'} ${brl(Math.abs(delta))}`}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>
          </Painel>
        </div>
      )}

      <Painel
        titulo="Protocolo a protocolo"
        subtitulo={dados ? `${numero(dados.linhas.length)} protocolo(s)` : undefined}
        rodape={dados?.linhas?.length > 0 && <Paginacao {...pag} rotulo="protocolos" />}
        acoes={
          <div className="d-flex flex-wrap gap-2 align-items-end">
            <SelecaoBuscavel
              rotulo="Operadora"
              largura={170}
              textoTodas="Todas"
              valor={params.get('operadora') || ''}
              opcoes={(opcoes?.operadoras || []).map((o) => ({ valor: o, texto: o }))}
              aoMudar={(v) => trocar('operadora', v)}
            />
            <SelecaoBuscavel
              rotulo="Competência"
              largura={150}
              textoTodas="Todas"
              valor={params.get('competencia') || ''}
              opcoes={(opcoes?.competencias || []).map((c) => ({ valor: c, texto: c }))}
              aoMudar={(v) => trocar('competencia', v)}
            />
            <SelecaoBuscavel
              rotulo="Situação"
              largura={160}
              textoTodas="Todas"
              valor={params.get('estado') || ''}
              opcoes={(opcoes?.estados || []).map((e) => ({
                valor: e,
                texto: ESTADO[e]?.rotulo || e,
              }))}
              aoMudar={(v) => trocar('estado', v)}
            />
          </div>
        }
      >
        {!dados ? (
          <LoadingSpinner />
        ) : dados.linhas.length === 0 ? (
          <Vazio
            icone="bi-cash-stack"
            titulo="Nenhum protocolo neste recorte"
            descricao="Lance um recebimento para que os protocolos apareçam aqui."
          />
        ) : (
          <>
          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between px-3 pt-3">
            <span style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
              {abertos.size > 0
                ? `${numero(abertos.size)} protocolo(s) expandido(s)`
                : 'Clique no  >  para ver as competências de um protocolo'}
            </span>
            <div className="d-flex gap-2">
              <Botao
                variante="secundario"
                pequeno
                onClick={() => setAbertos(new Set(dados.linhas.map((l) => l.protocolo)))}
              >
                Expandir todos
              </Botao>
              <Botao
                variante="secundario"
                pequeno
                onClick={() => setAbertos(new Set())}
                disabled={abertos.size === 0}
              >
                Recolher todos
              </Botao>
            </div>
          </div>
          <Tabela>
            <thead>
              <tr>
                {/* A coluna de competência SAIU deste nível: um protocolo
                    recebe em várias, e no primeiro nível a pergunta é
                    "quanto falta", que é uma linha só. As competências
                    aparecem ao expandir. */}
                <Th campo="protocolo" estado={ord}>Protocolo</Th>
                <Th campo="operadora" estado={ord}>Operadora</Th>
                <Th campo="total_cobrado" estado={ord} alinhar="right">
                  Cobrado
                  <Ajuda titulo="Total cobrado" origem="Bioslab · soma de vlr_contratante">
                    O valor que o laboratório faturou naquele protocolo, somando todas as
                    guias dele — inclusive as que ainda não apareceram em nenhum
                    demonstrativo. É o tamanho do bolo.
                  </Ajuda>
                </Th>
                <Th campo="recebido" estado={ord} alinhar="right">
                  Recebido
                </Th>
                <Th campo="aguardando_revisao" estado={ord} alinhar="right">
                  Em revisão
                  <Ajuda
                    titulo="Aguardando revisão"
                    origem="arquivo com status diferente de confirmado"
                  >
                    Dinheiro já extraído de um arquivo que ainda não passou pela
                    revisão humana. <strong>Não entra na conta</strong> — mas também
                    não some: sem esta coluna, o saldo cairia sozinho no dia em que
                    alguém confirmasse a revisão, sem nada ligando uma coisa à outra.
                  </Ajuda>
                </Th>
                <Th campo="glosado" estado={ord} alinhar="right">
                  Glosado
                </Th>
                <Th campo="saldo" estado={ord} alinhar="right">
                  Saldo
                  <Ajuda titulo="Saldo a receber" origem="cobrado − recebido − glosado">
                    O que foi faturado, a operadora ainda não pagou e também não recusou.
                    Quando a conta dá negativo o sistema não chama de saldo: marca como
                    inconsistente, porque significa que pagaram mais do que o Bioslab
                    registra como cobrado.
                  </Ajuda>
                </Th>
                <Th campo="percentual_recebido" estado={ord}>Composição</Th>
                <Th campo="meses_sem_receber" estado={ord} alinhar="right">
                  Parado há
                  <Ajuda titulo="Há quanto tempo não entra nada" origem="última competência recebida">
                    Meses desde a última competência em que este protocolo
                    recebeu. É o que separa um saldo de fluxo normal de um que já
                    merece cobrança — a lista ordenada só por valor não distingue
                    os dois.
                    <br />
                    <br />
                    Vem da competência, e não do número do protocolo:{' '}
                    <span className="ff-mono">476.03.2026</span> <em>parece</em>{' '}
                    sequencial.mês.ano, mas isso nunca foi confirmado, e errar o mês
                    mudaria a leitura de "normal" para "inadimplência".
                  </Ajuda>
                </Th>
                <Th campo="estado" estado={ord}>Situação</Th>
                <Th alinhar="right">{''}</Th>
              </tr>
            </thead>
            <tbody>
              {pag.paginados.map((l) => (
                <Fragment key={l.protocolo}>
                <tr
                  className={abertos.has(l.protocolo) ? 'ff-linha-mae-aberta' : undefined}
                >
                  <td className="col-codigo">
                    {/* O chevron é o controle, e precisa ser botão de
                        verdade: a linha inteira não é clicável porque cada
                        célula aqui é um número que a pessoa vai querer
                        selecionar e copiar. */}
                    <button
                      type="button"
                      className="ff-expandir"
                      onClick={() => alternar(l.protocolo)}
                      aria-expanded={abertos.has(l.protocolo)}
                      aria-label={
                        abertos.has(l.protocolo)
                          ? `Recolher competências de ${l.protocolo}`
                          : `Ver competências de ${l.protocolo}`
                      }
                      title={
                        l.qtd_competencias
                          ? `${l.qtd_competencias} competência(s) com recebimento`
                          : 'Sem recebimento registrado'
                      }
                    >
                      <i
                        className={`bi bi-chevron-${abertos.has(l.protocolo) ? 'down' : 'right'}`}
                        aria-hidden="true"
                      />
                    </button>
                    {l.protocolo}
                    {l.total_suspeito && (
                      <Ajuda titulo="Total suspeito" origem="contagem devolvida pelo Bioslab">
                        A consulta trouxe mais linhas do que itens distintos, o que
                        acontece quando um documento de cobrança entra em mais de uma
                        composição de fatura — refaturamento. O total pode estar inflado;
                        confira antes de cobrar por ele.
                      </Ajuda>
                    )}
                  </td>
                  <td className="col-meta">{l.operadora || '—'}</td>
                  <td className="col-valor">
                    {l.total_cobrado === null ? (
                      <span style={{ color: 'var(--ff-border-field)' }}>—</span>
                    ) : (
                      brl(l.total_cobrado)
                    )}
                  </td>
                  <td className="col-valor">{brl(l.recebido)}</td>
                  <td className="col-valor">
                    {Number(l.aguardando_revisao) > 0 ? (
                      <span style={{ color: 'var(--ff-muted)' }}>
                        {brl(l.aguardando_revisao)}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--ff-border-field)' }}>—</span>
                    )}
                  </td>
                  <td className="col-valor">
                    {Number(l.glosado) > 0 ? (
                      <span style={{ color: 'var(--ff-error-ink)' }}>{brl(l.glosado)}</span>
                    ) : (
                      <span style={{ color: 'var(--ff-border-field)' }}>—</span>
                    )}
                  </td>
                  <td className="col-valor">
                    {l.saldo === null ? (
                      <span style={{ color: 'var(--ff-border-field)' }}>—</span>
                    ) : (
                      <strong>{brl(l.saldo)}</strong>
                    )}
                  </td>
                  <td>
                    <Composicao linha={l} />
                  </td>
                  <td className="col-qtd">
                    {l.meses_sem_receber === null ? (
                      <span style={{ color: 'var(--ff-border-field)' }}>—</span>
                    ) : (
                      <span
                        style={{
                          color:
                            l.meses_sem_receber >= 6 && l.estado === 'a_receber'
                              ? 'var(--ff-error-ink)'
                              : undefined,
                          fontWeight: l.meses_sem_receber >= 6 ? 600 : undefined,
                        }}
                      >
                        {l.meses_sem_receber === 0
                          ? 'este mês'
                          : `${l.meses_sem_receber} ${l.meses_sem_receber === 1 ? 'mês' : 'meses'}`}
                      </span>
                    )}
                  </td>
                  <td>
                    <Badge tom={ESTADO[l.estado]?.tom || 'arquivado'}>
                      {ESTADO[l.estado]?.rotulo || l.estado}
                    </Badge>
                  </td>
                  <td className="col-valor">
                    {/* O mesmo detalhe que a tela de Títulos abre: as três
                        contas, o gráfico mês a mês, a glosa por causa e a
                        linha do tempo. Daqui a pergunta é "por que este
                        protocolo ainda tem saldo?", e a resposta está lá. */}
                    <Botao
                      variante="secundario"
                      pequeno
                      icone="bi-clock-history"
                      onClick={(e) => {
                        e.stopPropagation();
                        navegar(`/titulos/${encodeURIComponent(l.protocolo)}`);
                      }}
                    >
                      Detalhe
                    </Botao>
                  </td>
                </tr>
                {abertos.has(l.protocolo) && <LinhasDaCompetencia linha={l} />}
                </Fragment>
              ))}
            </tbody>
          </Tabela>
          </>
        )}
      </Painel>

      {dados?.linhas?.some((l) => l.consultado_em) && (
        <p style={{ fontSize: 13, color: 'var(--ff-muted)', marginTop: 10 }}>
          Totais consultados no Bioslab em{' '}
          {formatDateTime(
            dados.linhas.map((l) => l.consultado_em).filter(Boolean).sort().at(-1)
          )}
          . O vínculo guia → protocolo é imutável depois de faturado, mas o total cobrado
          muda quando novas guias entram no protocolo — por isso vale atualizar antes de
          cobrar.
        </p>
      )}
    </>
  );
}
